import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CriticalPathAnalysis = ({ tasks, criticalPath, onExport }) => {
  const calculateTotalDuration = () => {
    return criticalPath?.reduce((sum, taskId) => {
      const task = tasks?.find(t => t?.id === taskId);
      return sum + (task?.duration || 0);
    }, 0);
  };

  const calculateSlack = (task) => {
    return task?.slack || 0;
  };

  const getSlackColor = (slack) => {
    if (slack === 0) return 'var(--color-error)';
    if (slack <= 8) return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Critical Path Analysis</h3>
          <p className="caption text-muted-foreground mt-1">
            Identifies the longest sequence of dependent tasks
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="Download"
          onClick={onExport}
        >
          Export Report
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="p-4 bg-muted rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Clock" size={20} color="var(--color-primary)" />
            <span className="text-sm font-medium text-muted-foreground">Total Duration</span>
          </div>
          <p className="text-2xl font-semibold text-foreground">{calculateTotalDuration()}h</p>
        </div>

        <div className="p-4 bg-muted rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="GitBranch" size={20} color="var(--color-error)" />
            <span className="text-sm font-medium text-muted-foreground">Critical Tasks</span>
          </div>
          <p className="text-2xl font-semibold text-foreground">{criticalPath?.length}</p>
        </div>

        <div className="p-4 bg-muted rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
            <span className="text-sm font-medium text-muted-foreground">At Risk</span>
          </div>
          <p className="text-2xl font-semibold text-foreground">
            {tasks?.filter(t => t?.status === 'delayed')?.length}
          </p>
        </div>

        <div className="p-4 bg-muted rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingUp" size={20} color="var(--color-success)" />
            <span className="text-sm font-medium text-muted-foreground">On Track</span>
          </div>
          <p className="text-2xl font-semibold text-foreground">
            {tasks?.filter(t => t?.status === 'in-progress' || t?.status === 'completed')?.length}
          </p>
        </div>
      </div>
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-foreground">Critical Path Tasks</h4>
        {criticalPath?.map((taskId, index) => {
          const task = tasks?.find(t => t?.id === taskId);
          if (!task) return null;

          const slack = calculateSlack(task);

          return (
            <div
              key={taskId}
              className="flex items-center gap-4 p-4 bg-muted rounded-lg"
            >
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-error text-white text-sm font-semibold">
                {index + 1}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{task?.name}</p>
                <div className="flex items-center gap-3 mt-1">
                  <span className="caption text-muted-foreground">{task?.duration}h</span>
                  <span className="caption text-muted-foreground">•</span>
                  <span className="caption text-muted-foreground capitalize">{task?.status}</span>
                  <span className="caption text-muted-foreground">•</span>
                  <span className="caption" style={{ color: getSlackColor(slack) }}>
                    Slack: {slack}h
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {task?.status === 'delayed' && (
                  <div className="px-2 py-1 bg-error/10 rounded text-error text-xs font-medium">
                    Delayed
                  </div>
                )}
                {slack === 0 && (
                  <div className="px-2 py-1 bg-error/10 rounded text-error text-xs font-medium">
                    No Slack
                  </div>
                )}
              </div>
              {index < criticalPath?.length - 1 && (
                <Icon name="ArrowDown" size={16} color="var(--color-muted-foreground)" />
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-6 p-4 bg-warning/10 rounded-lg border border-warning/20">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
          <div>
            <h4 className="text-sm font-medium text-foreground mb-1">Critical Path Impact</h4>
            <p className="caption text-muted-foreground">
              Any delay in critical path tasks will directly impact the project completion date.
              Monitor these tasks closely and allocate resources accordingly.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CriticalPathAnalysis;